import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as VehiclesActions from './vehicles.actions';
import {
  IAdministrator,
  IVehicle,
} from '@boma/supplier-portal/shared/entities';
import { IRole } from '@boma/client-shared';

export const VEHICLES_FEATURE_KEY = 'vehicles';

export interface VehicleState extends EntityState<IVehicle> {
  selectedId?: string | number; // which Vehicles record has been selected
  loaded: boolean; // has the Vehicles list been loaded
  error?: string | null; // last known error (if any)
  perPage: string | number;
  query: string | null;
  drivers: IAdministrator[];
  selectedAdministratorId: string | null;
  roles: IRole[];
  pagination: {};
  nextPage?: number;
}

export interface VehiclesPartialState {
  readonly [VEHICLES_FEATURE_KEY]: VehicleState;
}

export const vehiclesAdapter: EntityAdapter<IVehicle> = createEntityAdapter<
  IVehicle
>();

export const vehicleInitialState: VehicleState = vehiclesAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    page: 1,
    nextPage: 1,
    perPage: 25,
    query: '',
    drivers: [],
    roles: [],
    pagination: {},
    selectedAdministratorId: null,
  }
);

const vehiclesReducer = createReducer(
  vehicleInitialState,

  on(VehiclesActions.loadVehicles, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),

  on(VehiclesActions.loadVehiclesSuccess, (state, { payload }) =>
    vehiclesAdapter.setAll(payload.items, {
      ...state,
      loaded: true,
      pagination: {
        totalItems: payload.totalItems,
        totalPages: payload.totalPages,
        previousPage: payload.previousPage,
        nextPage: payload.nextPage,
      },
    })
  ),

  on(VehiclesActions.searchVehiclesSuccess, (state, { payload }) =>
    vehiclesAdapter.setAll(payload.items, {
      ...state,
      loaded: true,
      pagination: {
        totalItems: payload.totalItems,
        totalPages: payload.totalPages,
        previousPage: payload.previousPage,
        nextPage: payload.nextPage,
      },
    })
  ),

  on(VehiclesActions.loadVehiclesFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(VehiclesActions.createVehicleSuccess, (state, action) => {
    return vehiclesAdapter.upsertOne(action.vehicle, {
      ...state,
      error: '',
    });
  }),
  on(VehiclesActions.createVehicleFailure, (state, { error }) => {
    return {
      ...state,
      error: error,
    };
  }),
  on(VehiclesActions.loadVehicleSuccess, (state, { vehicle }) =>
    vehiclesAdapter.upsertOne(vehicle, {
      ...state,
      selectedId: vehicle.id,
    })
  ),
  on(VehiclesActions.loadVehicleFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(VehiclesActions.updatePerPage, (state, { perPage }) => ({
    ...state,
    perPage: perPage,
  })),
  on(VehiclesActions.updateVehiclePermissionsSuccess, (state, { payload }) => {
    console.log('Payload:', payload);
    return {
      ...state,
      loaded: true,
      assignedAdminRoles: payload,
    };
  }),
  on(VehiclesActions.updateVehiclePermissionsFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(VehiclesActions.updateVehicleSuccess, (state, { vehicle }) =>
    vehiclesAdapter.upsertOne(vehicle, {
      ...state,
      selectedId: vehicle.id,
    })
  ),
  on(VehiclesActions.updateVehicleFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(
    VehiclesActions.loadAdministratorsSuccess,
    (state, { administrators }) => ({
      ...state,
      drivers: administrators,
    })
  ),
  on(VehiclesActions.loadRolesSuccess, (state, { payload }) => ({
    ...state,
    roles: payload.items,
  })),
  on(
    VehiclesActions.createAdministratorSuccess,
    (state, { administrator }) => ({
      ...state,
      drivers: state.drivers?.length
        ? [...state.drivers].concat([administrator])
        : [administrator],
    })
  ),
  on(VehiclesActions.deleteAdministratorSuccess, (state, { userId }) => ({
    ...state,
    drivers: state.drivers?.length
      ? state.drivers.filter((a) => a.id !== userId)
      : [],
  })),
  on(VehiclesActions.loadAdministratorDetails, (state, { adminId }) => ({
    ...state,
    selectedAdministratorId: null,
  })),
  on(
    VehiclesActions.loadAdministratorDetailsSuccess,
    (state, { administrator }) => ({
      ...state,
      drivers: state.drivers?.length
        ? [...state.drivers.filter((a) => a.id !== administrator.id)].concat(
            administrator
          )
        : [administrator],
      selectedAdministratorId: administrator.id,
    })
  )
);

export function vehicleReducer(
  state: VehicleState | undefined,
  action: Action
) {
  return vehiclesReducer(state, action);
}
